<script setup lang="ts">
import { BAlert, BButton, BForm, BFormGroup, BFormInput, BFormSelect, BModal, BFormTextarea, BFormRadioGroup } from 'bootstrap-vue-next';
import { ref } from 'vue';

import type { EventSubmission } from '~/api/events';
import { createEvent } from '~/api/events';
import LoadingButton from '~/common/components/LoadingButton.vue';
import { EVENT_FORMAT_OPTIONS, EVENT_ROLE_OPTIONS, EVENT_SIZE_OPTIONS } from '~/common/Constants';
import { useVueData } from '~/common/useVueData';

interface EventForm {
  name: string;
  date: string | null;
  enddate: string | null;
  link: string | null;
  role: string | undefined;
  isVirtual: boolean | undefined;
  size: string | null;
  note: string;
}

const { approvedCommunityMember: isApprovedCommunityMember } = useVueData();

const blankForm: EventForm = {
  name: '',
  date: null,
  enddate: null,
  link: null,
  role: undefined,
  isVirtual: undefined,
  size: null,
  note: '',
};
const form = ref<EventForm>({ ...blankForm });
const loading = ref(false);
const error = ref<string | null>(null);
const model = defineModel<boolean>();
const emit = defineEmits<{ eventAdded: [event: EventSubmission] }>();

const addEvent = async (event: Event) => {
  event.preventDefault();

  const { name, date, enddate, link, role, isVirtual, size, note } = form.value;
  if (date === null || role === undefined || isVirtual === undefined || size === null) {
    return;
  }

  error.value = null;
  loading.value = true;

  const submission = { name, date, enddate, link, role, virtual: isVirtual, size, note };

  try {
    await createEvent(submission);
    emit('eventAdded', submission);

    model.value = false;
  } catch {
    error.value = 'Failed to add event. Please try again.';
  } finally {
    loading.value = false;
  }
};

const resetForm = () => {
  form.value = { ...blankForm };
  error.value = null;
};
</script>

<template>
  <BModal
    v-model="model"
    title="Add event"
    no-footer
    @hidden="resetForm"
  >
    <BAlert
      :model-value="!!error"
      variant="danger"
    >
      {{ error }}
    </BAlert>

    <BForm
      v-if="isApprovedCommunityMember"
      @submit="addEvent"
    >
      <p class="text-muted small">
        Submissions are reviewed by the GitLab team before points are awarded.
      </p>
      <!-- Required Fields First -->
      <BFormGroup
        label="Name"
        class="mb-3"
      >
        <BFormInput
          v-model="form.name"
          required
          aria-required="true"
          data-test-id="event-modal-input-name"
        />
      </BFormGroup>
      <BFormGroup
        label="Date"
        class="mb-3"
      >
        <BFormInput
          v-model="form.date"
          type="date"
          required
          aria-required="true"
          data-test-id="event-modal-input-date"
        />
      </BFormGroup>
      <BFormGroup
        label="End date (optional)"
        class="mb-3"
      >
        <BFormInput
          v-model="form.enddate"
          type="date"
          aria-required="false"
          data-test-id="event-modal-input-enddate"
        />
      </BFormGroup>
      <BFormGroup
        label="Role"
        class="mb-3"
      >
        <BFormRadioGroup
          v-model="form.role"
          :options="EVENT_ROLE_OPTIONS"
          required
          aria-required="true"
          stacked
          data-test-id="event-modal-radio-role"
        />
      </BFormGroup>
      <BFormGroup
        label="Type"
        class="mb-3"
      >
        <BFormRadioGroup
          v-model="form.isVirtual"
          :options="EVENT_FORMAT_OPTIONS"
          required
          aria-required="true"
          stacked
          data-test-id="event-modal-radio-virtual"
        />
      </BFormGroup>
      <BFormGroup
        label="Size"
        class="mb-3"
      >
        <BFormSelect
          v-model="form.size"
          :options="EVENT_SIZE_OPTIONS"
          required
          aria-required="true"
          data-test-id="event-modal-select-size"
        />
      </BFormGroup>

      <!-- Optional Fields Last -->
      <BFormGroup
        label="Link (optional)"
        class="mb-3"
      >
        <BFormInput
          v-model="form.link"
          aria-required="false"
          data-test-id="event-modal-input-link"
        />
      </BFormGroup>
      <BFormGroup
        label="Note (optional)"
        class="mb-3"
      >
        <BFormTextarea
          v-model="form.note"
          type="text"
          aria-required="false"
          data-test-id="event-modal-input-note"
        />
      </BFormGroup>

      <LoadingButton
        type="submit"
        class="float-end"
        variant="primary"
        :loading="loading"
      >
        Add event
      </LoadingButton>
    </BForm>
    <template v-else>
      <p>To submit an event you must be an onboarded community member.</p>
      <BButton
        variant="primary"
        :to="{
          name: 'home',
          hash: '#start',
          query: { referrer: 'Submit an event' } }
        "
      >
        Start onboarding
      </BButton>
    </template>
  </BModal>
</template>
